/**
 * Service breakdown for a period, shared by both cloud tabs. When
 * `onOpenResources` is given each row offers a drill-down into that service's
 * top resources for the selected week.
 */

import { useTranslation } from 'react-i18next';
import { Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { FinopsServiceRow } from '../../../../shared/types';
import { EmptyState, fmtPct, fmtUSD } from './shared';

export function ServiceTable({
  title,
  subtitle,
  rows,
  onOpenResources,
  emptyTitle,
  emptyDescription,
}: {
  title: string;
  subtitle?: string;
  rows: FinopsServiceRow[];
  onOpenResources?: (service: string) => void;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const { t } = useTranslation();

  // Share is relative to what is shown — with an account filter applied, the
  // period total no longer matches this table's rows.
  const total = rows.reduce((s, r) => s + r.cost, 0);

  if (rows.length === 0) {
    return (
      <EmptyState
        title={emptyTitle ?? t('finops.cloud.services_empty_title')}
        description={emptyDescription ?? t('finops.cloud.month_empty_description')}
      />
    );
  }

  return (
    <Card className="p-5">
      <h3 className="text-sm font-semibold mb-3">
        {title}
        {subtitle && <span className="text-muted-foreground font-normal"> · {subtitle}</span>}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-2 pr-3">{t('finops.cloud.col_service')}</th>
              <th className="pr-3 text-right">{t('finops.cloud.col_cost')}</th>
              <th className="pr-3 text-right w-20">{t('finops.cloud.col_share')}</th>
              <th className="w-1/4" />
              {onOpenResources && <th className="w-24" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const share = total > 0 ? (r.cost / total) * 100 : 0;
              return (
                <tr key={r.service} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="py-2 pr-3 font-medium">{r.service}</td>
                  <td className="pr-3 text-right tabular-nums">{fmtUSD(r.cost)}</td>
                  <td className="pr-3 text-right tabular-nums text-muted-foreground">{fmtPct(share)}</td>
                  <td className="py-2">
                    <div className="h-2 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded bg-indigo-500" style={{ width: `${Math.min(100, share)}%` }} />
                    </div>
                  </td>
                  {onOpenResources && (
                    <td className="py-2 pl-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => onOpenResources(r.service)}
                      >
                        <Boxes className="h-3.5 w-3.5 mr-1" />
                        {t('finops.cloud.resources_action')}
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
